import { MetadataQrPrefill } from "components/MetadataQrPrefill";
import { Button, ButtonVariant } from "components/Form/Button";
import { Layout } from "components/Layout";
import { Contributor } from "types";
import { Field } from "components/Form/Field";
import { Input } from "components/Form/Input";
import { Checkbox } from "components/Form/Checkbox";
import { Select } from "components/Form/Select";
import clsx from "clsx";
import {
  CollectionDetailsFormValue,
  useCollectionDetailsForm,
} from "./useCollectionDetailsForm";
import { ReactNode, useEffect, useState, useCallback, useMemo } from "react";
import { Config, OtherLabel } from ".";
import { LabelRelation } from "types";
import { parseKeyValueLines } from "lib/key-value-lines";

interface CollectionDetailsProps {
  contributor: Contributor;
  onSubmit?: (data: CollectionDetailsFormValue) => void;
  setIsShow: (value: boolean) => void;
  children?: ReactNode;
  heading?: string;
  description?: ReactNode;
  continueLabel?: string;
  cancelLabel?: string;
  labelsOverride?: Config["labels"];
  labelRelationsOverride?: LabelRelation[];
  additionalDataRequiredOverride?: string[];
  knownValuesRefreshKey?: unknown;
  schemaFingerprint?: string;
}

export function CollectionDetailsForm(props: CollectionDetailsProps) {
  const {
    onSubmit,
    setIsShow,
    children,
    heading,
    description,
    continueLabel,
    cancelLabel,
    labelsOverride,
  } = props;
  const {
    formState,
    addManualLabel,
    labels,
    submit,
    isSubmitting,
    proposalError,
  } = useCollectionDetailsForm({
    onSubmit,
    labelsOverride,
    labelRelationsOverride: props.labelRelationsOverride,
    additionalDataRequiredOverride: props.additionalDataRequiredOverride,
    knownValuesRefreshKey: props.knownValuesRefreshKey,
    schemaFingerprint: props.schemaFingerprint,
  });

  useEffect(() => {
    if (labels === null) {
      setIsShow(false);
    }
  }, [labels, setIsShow]);

  const watchDevice = formState.watch("device");
  const watchLabels = formState.watch("labels");
  const watchData = formState.watch("data");
  const taskId = formState.watch("task");
  const [qrValues, setQrValues] = useState<Record<string, string>>({});
  useEffect(() => setQrValues({}), [taskId, props.schemaFingerprint]);
  const qrFields = Object.entries(labels || {}).flatMap(([id, field]) =>
    Array.isArray(field)
      ? []
      : [
          {
            id,
            title: field.title,
            type: field.field_type || "text",
            qrEnabled: field.qr_prefill === true,
            options: Object.entries(field.labels).map(([key, value]) => ({
              key,
              value,
            })),
          },
        ]
  );
  const clearQrSource = (id: string) =>
    setQrValues((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });

  return (
    <Layout
      className="grid gap-12 items-start content-start"
      toolbarClassName="!py-3"
      toolbar={
        <>
          <Button
            onClick={formState.handleSubmit(() => submit())}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : continueLabel || "Continue"}
          </Button>
          <button
            onClick={() => setIsShow(false)}
            className="text-sm text-858494 underline hover:text-191c20 transition-colors"
            disabled={isSubmitting}
          >
            {cancelLabel || "Cancel"}
          </button>
        </>
      }
    >
      {labels && (
        <>
          {children}

          <div className="grid gap-3 text-center">
            <h2 className="text-2xl font-semibold">
              {heading || "Add Collection Details"}
            </h2>

            <p className="text-858494">
              {description || (
                <>
                  Enter metadata for this capture or leave blank <br /> and
                  press 'Continue'.
                </>
              )}
            </p>
          </div>

          <form
            className="grid gap-6 bg-white border border-f4f7ff rounded-xl p-6"
            onSubmit={(e) => e.preventDefault()}
          >
            {qrFields.some((field) => field.qrEnabled) && (
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold">Collection details</h3>
                <MetadataQrPrefill
                  key={taskId || "collection-details"}
                  iconOnly
                  triggerLabel="Scan collection details"
                  collectionId={props.contributor}
                  disabled={isSubmitting}
                  fields={qrFields}
                  values={watchLabels || {}}
                  onApply={(values) => {
                    setQrValues((current) => ({ ...current, ...values }));
                    Object.entries(values).forEach(([id, value]) => {
                      formState.setValue(`labels.${id}`, value, {
                        shouldTouch: true,
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      formState.clearErrors(`labels.${id}`);
                    });
                  }}
                />
              </div>
            )}
            {proposalError && (
              <p
                className="rounded-xl bg-red-50 p-3 text-sm text-fd380f"
                role="alert"
              >
                {proposalError}
              </p>
            )}
            {labels.devices && labels.devices.length > 0 && (
              <Field
                label="Select applicable device"
                description={formState.formState.errors.device?.message}
                descriptionClassName={clsx({
                  "text-fd380f": formState.formState.errors.device?.message,
                })}
              >
                <Select
                  placeholder="Select label"
                  options={Object.fromEntries(
                    Object.entries(labels.devices).map(([, { key, label }]) => [
                      label,
                      key,
                    ])
                  )}
                  value={watchDevice}
                  onChange={(v) => {
                    formState.setValue("device", v as string, {
                      shouldValidate: true,
                    });
                    const camera_not_flipped = labels.devices.find(
                      (i) => i.key === v
                    )?.camera_not_flipped;

                    if (camera_not_flipped !== undefined) {
                      formState.setValue(
                        "camera_not_flipped",
                        camera_not_flipped
                      );
                    }
                    formState.clearErrors("device");
                  }}
                  renderValue={(value) =>
                    labels.devices.find((device) => device.key === value)
                      ?.label ?? value
                  }
                  renderListItem={({ value, label }) => `${label}:${value}`}
                />
              </Field>
            )}

            {Object.entries(labels)
              .filter(
                (i): i is [string, OtherLabel] =>
                  "title" in i[1] && !!i[1].field_type
              )
              .map(([id, field]) => (
                <Field
                  key={id}
                  label={
                    <span className="flex items-center justify-between gap-3">
                      <span>{field.title}</span>
                      {qrValues[id] && qrValues[id] === watchLabels?.[id] && (
                        <span className="shrink-0 text-sm font-normal text-3461fd">
                          From QR
                        </span>
                      )}
                    </span>
                  }
                  description={
                    formState.formState.errors["labels"]?.[id]?.message
                  }
                  descriptionClassName={clsx({
                    "text-fd380f":
                      formState.formState.errors["labels"]?.[id]?.message,
                  })}
                >
                  {/* Task-level field types only */}
                  {field.field_type === "text" && (
                    <Input
                      placeholder={`Enter ${field.title.toLowerCase()}`}
                      value={(watchLabels?.[id] as string) || ""}
                      onChange={(e) => {
                        clearQrSource(id);
                        formState.setValue(`labels.${id}`, e.target.value, {
                          shouldValidate: true,
                        });
                        formState.clearErrors(`labels.${id}`);
                      }}
                    />
                  )}

                  {field.field_type === "numeric" && (
                    <Input
                      type="number"
                      placeholder={`Enter ${field.title.toLowerCase()}`}
                      value={(watchLabels?.[id] as string) || ""}
                      onChange={(e) => {
                        clearQrSource(id);
                        formState.setValue(`labels.${id}`, e.target.value, {
                          shouldValidate: true,
                        });
                        formState.clearErrors(`labels.${id}`);
                      }}
                    />
                  )}

                  {field.field_type === "date" && (
                    <Input
                      type="date"
                      value={(watchLabels?.[id] as string) || ""}
                      onChange={(e) => {
                        clearQrSource(id);
                        formState.setValue(`labels.${id}`, e.target.value, {
                          shouldValidate: true,
                        });
                        formState.clearErrors(`labels.${id}`);
                      }}
                    />
                  )}

                  {field.field_type === "dropdown" && (
                    <Select
                      options={field.labels}
                      value={watchLabels?.[id]}
                      onChange={(v) => {
                        clearQrSource(id);
                        formState.setValue(`labels.${id}`, v, {
                          shouldValidate: true,
                          shouldTouch: true,
                        });
                        formState.clearErrors(`labels.${id}`);
                      }}
                      renderValue={(i) =>
                        [i]
                          .flat()
                          .map(
                            (value) =>
                              Object.entries(field.labels).find(
                                ([, storedValue]) => storedValue === value
                              )?.[0] ?? value
                          )
                          .join(", ")
                      }
                      renderListItem={(i) => `${i.label}:${i.value}`}
                      listClassName="max-h-[40vh] overflow-y-auto"
                    />
                  )}
                </Field>
              ))}

            {labels?.devices && labels.devices.length > 0 && (
              <Checkbox
                id="camera_not_flipped"
                {...formState.register("camera_not_flipped")}
                className="!bg-white border-2 border-8a898e order-2"
                containerClassName="font-medium text-16"
                checkboxClassName="order-2"
              >
                Camera not flipped
              </Checkbox>
            )}

            {watchData?.map?.((_, i) => (
              <Field key={i} label={`${i + 1}. Additional information`}>
                <Input {...formState.register(`data.${i}`)} />
              </Field>
            ))}

            <button
              className="border-2 rounded-xl border-black/20 py-2.5 px-2 text-858494"
              onClick={addManualLabel}
            >
              Add input field
            </button>
          </form>
        </>
      )}
      {!labels && "Loading..."}
    </Layout>
  );
}

export interface StepDetailsProps {
  stepNumber: number;
  stepSlice: any; // The step_details slice from Prismic
  contributor: Contributor;
  onSubmit: (stepNumber: number, data: Array<string>) => void;
  setIsShow: (value: boolean) => void;
}

export function StepDetailsForm(props: StepDetailsProps) {
  const { stepNumber, stepSlice, onSubmit, setIsShow } = props;
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const stepDetailsFields = useMemo(() => stepSlice?.items || [], [stepSlice]);

  const handleSubmit = useCallback(() => {
    setErrors({});
    let hasErrors = false;

    // Validate required fields
    stepDetailsFields.forEach((field: any) => {
      if (
        field.is_required &&
        (!formData[field.label_id] || formData[field.label_id].trim() === "")
      ) {
        setErrors((prev) => ({
          ...prev,
          [field.label_id]: `${field.label_title || "Field"} is required`,
        }));
        hasErrors = true;
      }
    });

    if (hasErrors) {
      return;
    }

    // Convert form data to array format "label_id: value"
    const stepDetails = stepDetailsFields
      .map((field: any) => {
        const value = formData[field.label_id];
        return value && value.trim() !== ""
          ? `${field.label_id}: ${value}`
          : "";
      })
      .filter((value: string) => value && value.trim() !== "");

    onSubmit(stepNumber, stepDetails);
  }, [formData, stepDetailsFields, stepNumber, onSubmit]);

  const handleFieldChange = useCallback(
    (fieldId: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [fieldId]: value,
      }));

      // Clear error when user starts typing
      if (errors[fieldId]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldId];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const renderField = useCallback(
    (field: any) => {
      const fieldId = field.label_id;
      const fieldType = field.label_type;
      const fieldTitle = field.label_title;
      const dropdownOptions = field.dropdown_options;

      switch (fieldType) {
        case "text":
          return (
            <Input
              key={fieldId}
              placeholder={`Enter ${fieldTitle.toLowerCase()}`}
              value={formData[fieldId] || ""}
              onChange={(e) => handleFieldChange(fieldId, e.target.value)}
            />
          );

        case "numeric":
          return (
            <Input
              key={fieldId}
              type="number"
              placeholder={`Enter ${fieldTitle.toLowerCase()}`}
              value={formData[fieldId] || ""}
              onChange={(e) => handleFieldChange(fieldId, e.target.value)}
            />
          );

        case "date":
          return (
            <Input
              key={fieldId}
              type="date"
              value={formData[fieldId] || ""}
              onChange={(e) => handleFieldChange(fieldId, e.target.value)}
            />
          );

        case "dropdown":
          if (!dropdownOptions) return null;

          const options = parseKeyValueLines(dropdownOptions);

          const optionsObject = options.reduce(
            (
              acc: Record<string, string>,
              opt: { key: string; value: string }
            ) => {
              acc[opt.value] = opt.key;
              return acc;
            },
            {}
          );

          return (
            <Select
              key={fieldId}
              value={formData[fieldId] || ""}
              onChange={(value) => handleFieldChange(fieldId, value as string)}
              options={optionsObject}
              placeholder={`Select ${fieldTitle.toLowerCase()}`}
              renderListItem={(item) => `${item.value}:${item.label}`}
            />
          );

        default:
          return null;
      }
    },
    [formData, handleFieldChange]
  );

  return (
    <Layout className="space-y-8">
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-24 font-semibold">Step {stepNumber} Details</h1>
          <p className="text-858494 mt-2">
            {stepSlice?.primary?.section_title &&
            typeof stepSlice.primary.section_title === "string"
              ? stepSlice.primary.section_title
              : stepSlice.primary.section_title?.[0]?.text ||
                "Enter step-specific information before capture."}
          </p>
        </div>

        <div className="space-y-4">
          {stepDetailsFields.map((field: any) => (
            <Field
              key={field.label_id}
              label={field.label_title}
              description={errors[field.label_id]}
              descriptionClassName={clsx({
                "text-fd380f": errors[field.label_id],
              })}
            >
              {renderField(field)}
            </Field>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          <Button onClick={handleSubmit}>
            Continue with Step {stepNumber}
          </Button>
          <Button variant={ButtonVariant.FLAT} onClick={() => setIsShow(false)}>
            Cancel
          </Button>
        </div>
      </div>
    </Layout>
  );
}
